import React, { useCallback, useEffect, useState } from 'react';
import CustomTable from '../../../../components/CustomTable/CustomTable';
import useNominaRar from '../../hooks/useNominaRar';
import { useAppSelector } from '../../../../hooks/reduxHooks';
import {
    Box,
    Button,
    Collapse,
    Grid,
    IconButton,
    styled,
    Tooltip,
    Typography
} from '@mui/material';
import BackdropComponent from '../../../../components/Backdrop/BackdropComponent';
import useGlobalFilters from '../../../../hooks/useGlobalFilters';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../../../../contexts/TitleContext';
import { useGetResponsablesByPresentacionIdQuery } from '../../../../services/presentacionesRarApi';
import useToggle from '../../../../components/Cabeceras/hooks/useToggle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getDateTextField } from '../../../../utils/common';
import { RarViewFilters } from '../RarViewFilters';
import SearchToolbar from '../../../../components/SearchToolbar/SearchToolbar';
import {
    CUSTOM_HABILITATION_TITLE_OPTION,
    isExisitingHabilitationTitle
} from '../../../../commons/presentations';

export enum NOMINA_STATE {
    OK = 'OK',
    INFO = 'INFO',
    ERROR = 'ERROR'
}

interface Column {
    title: string;
    render: (row: any) => React.ReactNode;
    align?: 'left' | 'center' | 'right';
}

/*
 * Fuera del componente: declarados adentro eran un tipo nuevo en cada render,
 * React desmontaba y volvía a montar el bloque del responsable y el Collapse
 * abría y cerraba sin animación.
 */
const ExpandableItemWrapper = styled('div')({
    marginLeft: '25px'
});

const TitleWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px'
});

const RarView = () => {
    const presentacionItem = useAppSelector((state) => state.rarPresentacion.presentacionItem);
    const { setTitle } = useTitle();

    const { t } = useTranslation('presentacionesRar');
    const {
        filters,
        setFilters,
        handleSubmitSearch,
        clearFilters,
        extraFilters,
        handleChangeExtraFilters
    } = useGlobalFilters();

    const [isExpanded, toggleExpanded] = useToggle(true);
    const {
        presentacionDetalle,
        isLoadingDetalle,
        isLoadingDescarga,
        isLoadingCambioEstado,
        handleClickVolver,
        fetchDetalleNomina
    } = useNominaRar(filters, presentacionItem?.id);

    const {
        data: responsables,
        isLoading,
        error
    } = useGetResponsablesByPresentacionIdQuery(
        { idPresentacion: presentacionItem?.id ?? 0 },
        { skip: !presentacionItem }
    );

    useEffect(() => {
        if (presentacionItem) {
            fetchDetalleNomina();
        }
    }, [presentacionItem, fetchDetalleNomina]);

    useEffect(() => {
        if (presentacionItem) {
            localStorage.setItem('selectedPresentationId', presentacionItem?.id.toString());
        }
    }, [presentacionItem]);

    useEffect(() => {
        setTitle(t('headerRar.title'));
    }, [setTitle]);

    const iconButtonStyle = {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        marginLeft: '15px',
        alignSelf: 'center'
    };

    const renderResponsibleField = (label: string, value: React.ReactNode) => (
        <>
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1E272E' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#3f4b59' }}>{value}</Typography>
        </>
    );

    const columns: Column[] = [
        { title: t('tableNominaDetail.columns.cuil'), render: (row) => row.cuil },
        {
            title: t('tableNominaDetail.columns.fullName'),
            render: (row) => `${row.nombre} ${row.apellido}`
        },
        {
            title: t('tableNominaDetail.columns.ingressDate'),
            render: (row) => getDateTextField(row.fechaIngreso)
        },
        {
            title: t('tableNominaDetail.columns.expositionDate'),
            render: (row) => getDateTextField(row.fechaInicioExposicion)
        },
        { title: t('tableNominaDetail.columns.sector'), render: (row) => row.sector },
        { title: t('tableNominaDetail.columns.Job'), render: (row) => row.puesto },
        {
            title: t('tableNominaDetail.columns.risk'),
            render: (row: any) => {
                if (!row?.riesgos || !Array.isArray(row.riesgos) || row.riesgos.length === 0) {
                    return '-';
                }

                const formattedValues = row.riesgos
                    .map((risk: any) =>
                        risk.codigo === '00000' ? t('labels.notExposed') : risk.codigo
                    )
                    .filter((value: any) => value);

                return formattedValues.length > 0 ? formattedValues.join(', ') : '-';
            }
        }
    ];

    return (
        <div>
            <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
                alignContent="center"
                spacing={3}
                sx={{
                    '& .MuiGrid-root': {
                        width: '100%'
                    }
                }}
            >
                <Box sx={{ width: '100%' }}>
                    <ExpandableItemWrapper sx={{ background: 'white', pl: 3, mt: '-30px' }}>
                        <TitleWrapper>
                            <Typography variant="h3" sx={{ fontWeight: '600', color: '#1E272E' }}>
                                {t('responsableData.title')}
                            </Typography>
                            <IconButton onClick={toggleExpanded} style={iconButtonStyle}>
                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </TitleWrapper>
                        <Collapse in={isExpanded}>
                            {responsables?.data
                                ?.filter(
                                    (data: { cargo: { codigo: string } }) =>
                                        data?.cargo?.codigo === 'R'
                                )
                                .map(
                                    (
                                        data: {
                                            nombre: string;
                                            apellido: string;
                                            cuit: string;
                                            cargo: { nombre: string };
                                            tipoContratacion: string | { nombre: string };
                                            titulo: string;
                                            matricula: string;
                                            entidadOtorgante: string;
                                        },
                                        index: number
                                    ) => (
                                        <Grid
                                            container
                                            spacing={2}
                                            sx={{ pb: 4, pl: 2 }}
                                            key={`${data.cuit}-${index}`}
                                        >
                                            <Grid item xs={12} sm={6} md={3} lg={1.5}>
                                                {renderResponsibleField(
                                                    t('responsableData.name'),
                                                    `${data.nombre || ''} ${
                                                        data.apellido || ''
                                                    }`.trim()
                                                )}
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3} lg={1.5}>
                                                {renderResponsibleField(
                                                    t('responsableData.CUIT'),
                                                    data.cuit || 'N/A'
                                                )}
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3} lg={1.5}>
                                                {renderResponsibleField(
                                                    t('responsableData.cargo'),
                                                    data.cargo?.nombre || 'N/A'
                                                )}
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3} lg={1.5}>
                                                {renderResponsibleField(
                                                    t('responsableData.status'),
                                                    typeof data.tipoContratacion === 'string'
                                                        ? data.tipoContratacion
                                                        : data.tipoContratacion?.nombre || 'N/A'
                                                )}
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3} lg={1.5}>
                                                {renderResponsibleField(
                                                    t('responsableData.tituloHabilitante'),
                                                    isExisitingHabilitationTitle(data?.titulo) ? (
                                                        data?.titulo
                                                    ) : (
                                                        <span>
                                                            {data?.titulo
                                                                ? CUSTOM_HABILITATION_TITLE_OPTION
                                                                : 'N/A'}
                                                            {data?.titulo && (
                                                                <Tooltip
                                                                    title={
                                                                        data?.titulo ||
                                                                        'Missing information'
                                                                    }
                                                                >
                                                                    <span
                                                                        style={{
                                                                            textDecoration:
                                                                                'underline',
                                                                            cursor: 'pointer'
                                                                        }}
                                                                    ></span>
                                                                </Tooltip>
                                                            )}
                                                        </span>
                                                    )
                                                )}
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3} lg={1.5}>
                                                {renderResponsibleField(
                                                    t('responsableData.matriculaNumber'),
                                                    data.matricula || 'N/A'
                                                )}
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3} lg={1.5}>
                                                {renderResponsibleField(
                                                    t('responsableData.entidadOtorgante'),
                                                    data.entidadOtorgante || 'N/A'
                                                )}
                                            </Grid>
                                        </Grid>
                                    )
                                )}
                        </Collapse>
                    </ExpandableItemWrapper>
                </Box>
                <Grid item xs={12}>
                    <SearchToolbar
                        inputSearchName=""
                        onChange={() => {}}
                        onClick={handleSubmitSearch}
                        showSearchInput={false}
                        clearFilters={clearFilters}
                    >
                        <RarViewFilters
                            extraFilters={extraFilters}
                            handleChangeExtraFilters={handleChangeExtraFilters}
                            t={t}
                        />
                    </SearchToolbar>
                </Grid>

                <Grid item xs={12} lg={12}>
                    <CustomTable
                        columns={columns}
                        isLoading={isLoadingDetalle}
                        data={presentacionDetalle?.data?.data ?? []}
                        count={presentacionDetalle?.data?.count}
                        filters={filters}
                        setFilters={setFilters}
                        noInfoTitle={t('tableNominaDetail.noResultsTitle')}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    lg={12}
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Button
                        variant="outlined"
                        onClick={handleClickVolver}
                        style={{
                            borderRadius: '8px',
                            height: '48px',
                            width: 'auto'
                        }}
                    >
                        {t('buttons.goBack')}
                    </Button>
                </Grid>
            </Grid>
            <BackdropComponent
                loading={isLoadingDetalle || isLoadingDescarga || isLoadingCambioEstado}
            />
        </div>
    );
};

export default RarView;
